import { useEffect, useState } from 'react';
import { Button, Form, Modal, Spinner } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import Select from 'react-select';
import CustomAlert from '../../components/CustomAlert';
import config from '../../config/Constant';
import { postRequest } from '../../helpers/apiHandler';
import CloseIcon from '../../layouts/SVG/CloseIcon';

const categoryOptions = [
  { value: 'marketing', label: 'Marketing' },
  { value: 'utility', label: 'Utility' },
  { value: 'authentication', label: 'Authentication' },
];

const WhatsAppPricingRatesCreateModal = ({ show, onClose, getPricingRatesData }) => {
  const { access_token } = useSelector((state) => state.auth.user);

  const [countryCode, setCountryCode] = useState('');
  const [countryName, setCountryName] = useState('');
  const [category, setCategory] = useState(null);
  const [cost, setCost] = useState('');
  const [effectiveFrom, setEffectiveFrom] = useState('');
  const [loading, setLoading] = useState(false);
  const [alertState, setAlertState] = useState({
    show: false,
    variant: 'danger',
    message: '',
  });

  const resetForm = () => {
    setCountryCode('');
    setCountryName('');
    setCategory(null);
    setCost('');
    setEffectiveFrom('');
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!countryCode || !countryName || !category || !cost) {
      setAlertState({
        message: 'Please fill in all required fields',
        show: true,
        variant: 'danger',
      });
      return;
    }

    const costValue = parseFloat(cost);
    if (isNaN(costValue) || costValue < 0) {
      setAlertState({
        message: 'Please enter a valid cost (0 or greater)',
        show: true,
        variant: 'danger',
      });
      return;
    }

    setLoading(true);

    const data = {
      country_code: countryCode.toUpperCase(),
      country_name: countryName,
      pricing_category: category.value,
      cost_per_conversation: costValue,
      effective_from: effectiveFrom || new Date().toISOString(),
    };

    postRequest('/v2/admin/whatsapp/pricing-rates', data, access_token.token, (res) => {
      setLoading(false);
      if (res.success) {
        setAlertState({
          message: 'Pricing rate created successfully',
          show: true,
          variant: 'success',
        });
        setTimeout(() => {
          handleClose();
          getPricingRatesData();
        }, 1000);
      } else {
        setAlertState({
          message: res.message || 'Failed to create pricing rate',
          show: true,
          variant: 'danger',
        });
      }
    });
  };

  useEffect(() => {
    if (alertState.show) {
      setTimeout(() => {
        setAlertState({ message: '', show: false, variant: '' });
      }, config.notify_time);
    }
  }, [alertState]);

  const selectStyles = {
    control: (base) => ({
      ...base,
      borderColor: '#e0e0e0',
      '&:hover': { borderColor: '#3b82f6' },
    }),
    option: (base, state) => ({
      ...base,
      backgroundColor: state.isSelected ? '#3b82f6' : state.isFocused ? '#e0e7ff' : 'white',
      color: state.isSelected ? 'white' : '#333',
    }),
  };

  return (
    <Modal show={show} onHide={handleClose} centered>
      <Modal.Body style={{ padding: '24px' }}>
        <CloseIcon handleModalClose={handleClose} />
        <Modal.Title as="h4" style={{ margin: '8px 0 24px' }}>
          Add New Pricing Rate
        </Modal.Title>

        <CustomAlert
          message={alertState.message}
          show={alertState.show}
          variant={alertState.variant}
          closeAlert={() => setAlertState({ show: false, variant: 'danger', message: '' })}
        />

        <Form onSubmit={handleSubmit}>
          <Form.Group className="mb-3">
            <Form.Label>Country Code *</Form.Label>
            <Form.Control
              type="text"
              value={countryCode}
              onChange={(e) => setCountryCode(e.target.value)}
              placeholder="e.g., US, MY, SG"
              maxLength={2}
              required
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Country Name *</Form.Label>
            <Form.Control
              type="text"
              value={countryName}
              onChange={(e) => setCountryName(e.target.value)}
              placeholder="e.g., United States, Malaysia"
              required
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Category *</Form.Label>
            <Select
              value={category}
              onChange={setCategory}
              options={categoryOptions}
              styles={selectStyles}
              placeholder="Select category..."
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Cost per Conversation (USD) *</Form.Label>
            <Form.Control
              type="number"
              step="0.0001"
              min="0"
              value={cost}
              onChange={(e) => setCost(e.target.value)}
              placeholder="Enter cost per conversation"
              required
            />
            <Form.Text className="text-muted">
              Enter the cost in USD with up to 4 decimal places
            </Form.Text>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Effective From</Form.Label>
            <Form.Control
              type="date"
              value={effectiveFrom}
              onChange={(e) => setEffectiveFrom(e.target.value)}
            />
            <Form.Text className="text-muted">
              Leave empty to use current date
            </Form.Text>
          </Form.Group>

          <div className="d-flex justify-content-end gap-2 mt-4">
            <Button variant="outline-secondary" onClick={handleClose} disabled={loading}>
              Cancel
            </Button>
            <Button variant="primary" type="submit" disabled={loading}>
              {loading ? (
                <>
                  <Spinner animation="border" size="sm" className="me-2" />
                  Creating...
                </>
              ) : (
                'Create'
              )}
            </Button>
          </div>
        </Form>
      </Modal.Body>
    </Modal>
  );
};

export default WhatsAppPricingRatesCreateModal;
